import * as React2 from 'react';
import { PermissionProvider, usePermissions, cn, Badge, Avatar, DataTableColumnHeader, ResourceTable, TableFacets, Dialog, DialogTrigger, Button, DialogContent, DialogHeader, DialogTitle, DialogDescription, Label, Input, Checkbox, Textarea, DialogFooter, Spinner, EmptyState, RoleEditor as RoleEditor$1, DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, ErrorState, Skeleton, AuditTrail, Select, SelectTrigger, SelectValue, SelectContent, SelectItem, ErrorBoundary, notifySuccess, notifyError, Popover, PopoverTrigger, PopoverContent, Tabs, TabsList, TabsTrigger, TabsContent, AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel } from '@authe/ui-core';
export { PermissionMatrixEditor, PermissionScopePicker } from '@authe/ui-core';
import { TeamRoleKey, MemberStatus, MEMBER_STATUS_LABEL, teamKey, TeamResource, memberInitials, isValidEmail, INVITE_STATUS_LABEL, toActivityLogEvents, sameTeam, resolveRoleKey, isInviteActionable } from '@authe/fe-commons/teams';
export { INVITE_STATUS_LABEL, InviteStatus, MEMBER_STATUS_LABEL, MemberStatus, TeamResource, TeamRoleKey, effectiveMatrix, isInviteActionable, isValidEmail, memberInitials, memberPermissionSet, resolveRoleKey, rolesForMember, sameTeam, teamKey, teamTenantKey, toActivityLogEvents } from '@authe/fe-commons/teams';
import { countPermissions } from '@authe/fe-commons/auth';
export { ACTION_LABEL, DEFAULT_ACTIONS, DEFAULT_RESOURCE_GROUPS, MANAGE_ACTION, countPermissions, enforceFromRolePermissions, grantedGroupCount, groupFullyGranted, hasPermission, mergeMatrices, permissionSetFromRolePermissions, setGroupActions, togglePermission } from '@authe/fe-commons/auth';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { createLogger } from '@authe/fe-commons/logging';
import { formatRelativeTime } from '@authe/fe-commons/time';
import { unwrapData, unwrapResponse } from '@authe/fe-commons/api';

// src/TeamManagementPanel.tsx
function mergeDefaultRoles(custom, defaults = []) {
  if (defaults.length === 0) return custom;
  const have = new Set(custom.map((role) => role.name.toLowerCase()));
  return [...custom, ...defaults.filter((role) => !have.has(role.name.toLowerCase()))];
}
var PRIVILEGE_ORDER = [
  TeamRoleKey.OWNER,
  TeamRoleKey.ADMIN,
  TeamRoleKey.MEMBER,
  TeamRoleKey.VIEWER
];
function privilegeRank(role) {
  const key = resolveRoleKey(role.name);
  const index = key ? PRIVILEGE_ORDER.indexOf(key) : -1;
  return index === -1 ? PRIVILEGE_ORDER.length : index;
}
function groupRolesBySource(roles) {
  const systemRoles = roles.filter((role) => role.system).sort((a, b) => privilegeRank(a) - privilegeRank(b));
  const customRoles = roles.filter((role) => !role.system);
  const groups = [
    { key: "default", label: "Default roles", roles: systemRoles },
    { key: "custom", label: "Custom roles", roles: customRoles }
  ];
  return groups.filter((group) => group.roles.length > 0);
}
function useAsyncResource(loader, deps) {
  const [data, setData] = React2.useState();
  const [isLoading, setIsLoading] = React2.useState(true);
  const [error, setError] = React2.useState(null);
  const [tick, setTick] = React2.useState(0);
  const loaderRef = React2.useRef(loader);
  loaderRef.current = loader;
  React2.useEffect(() => {
    let alive = true;
    setIsLoading(true);
    setError(null);
    loaderRef.current().then((result) => {
      if (alive) {
        setData(result);
        setIsLoading(false);
      }
    }).catch((err) => {
      if (alive) {
        setError(err);
        setIsLoading(false);
      }
    });
    return () => {
      alive = false;
    };
  }, [...deps, tick]);
  const refetch = React2.useCallback(() => setTick((value) => value + 1), []);
  return { data, isLoading, error, refetch, setData };
}
var TeamDataContext = React2.createContext(null);
var upsertMember = (members, next) => (members ?? []).map((member) => member.id === next.id ? next : member);
function countMembers(members, roleId) {
  return (members ?? []).reduce((n, member) => member.roleIds.includes(roleId) ? n + 1 : n, 0);
}
function TeamDataProvider({
  api,
  team,
  auditQuery,
  children
}) {
  const key = teamKey(team);
  const membersRes = useAsyncResource(() => api.listMembers(team), [key]);
  const rolesRes = useAsyncResource(() => api.listRoles(team), [key]);
  const invitesRes = useAsyncResource(() => api.listInvites(team), [key]);
  const auditRes = useAsyncResource(
    () => api.listAuditEvents(team, auditQuery),
    [key, auditQuery?.limit, auditQuery?.cursor]
  );
  const setMembers = membersRes.setData;
  const setRoles = rolesRes.setData;
  const setInvites = invitesRes.setData;
  const refreshAudit = auditRes.refetch;
  const updateMemberRoles = React2.useCallback(
    async (memberId, roleIds) => {
      const updated = await api.updateMemberRoles(team, memberId, roleIds);
      setMembers((prev) => upsertMember(prev, updated));
      refreshAudit();
    },
    [api, team, setMembers, refreshAudit]
  );
  const setMemberStatus = React2.useCallback(
    async (memberId, status) => {
      const updated = await api.setMemberStatus(team, memberId, status);
      setMembers((prev) => upsertMember(prev, updated));
      refreshAudit();
    },
    [api, team, setMembers, refreshAudit]
  );
  const removeMember = React2.useCallback(
    async (memberId) => {
      await api.removeMember(team, memberId);
      setMembers((prev) => (prev ?? []).filter((member) => member.id !== memberId));
      refreshAudit();
    },
    [api, team, setMembers, refreshAudit]
  );
  const createRole = React2.useCallback(
    async (input) => {
      const role = await api.createRole(team, input);
      setRoles((prev) => [...prev ?? [], role]);
      refreshAudit();
      return role;
    },
    [api, team, setRoles, refreshAudit]
  );
  const updateRole = React2.useCallback(
    async (roleId, patch) => {
      const role = await api.updateRole(team, roleId, patch);
      setRoles((prev) => (prev ?? []).map((existing) => existing.id === roleId ? role : existing));
      refreshAudit();
      return role;
    },
    [api, team, setRoles, refreshAudit]
  );
  const removeRole = React2.useCallback(
    async (roleId) => {
      await api.deleteRole(team, roleId);
      setRoles((prev) => (prev ?? []).filter((role) => role.id !== roleId));
      setMembers(
        (prev) => (prev ?? []).map(
          (member) => member.roleIds.includes(roleId) ? { ...member, roleIds: member.roleIds.filter((id) => id !== roleId) } : member
        )
      );
      refreshAudit();
    },
    [api, team, setRoles, setMembers, refreshAudit]
  );
  const invite = React2.useCallback(
    async (input) => {
      const created = await api.inviteMember(team, input);
      setInvites((prev) => [created, ...prev ?? []]);
      refreshAudit();
      return created;
    },
    [api, team, setInvites, refreshAudit]
  );
  const revokeInvite = React2.useCallback(
    async (inviteId) => {
      await api.revokeInvite(team, inviteId);
      setInvites((prev) => (prev ?? []).filter((existing) => existing.id !== inviteId));
      refreshAudit();
    },
    [api, team, setInvites, refreshAudit]
  );
  const resendInvite = React2.useCallback(
    async (inviteId) => {
      const updated = await api.resendInvite(team, inviteId);
      setInvites((prev) => (prev ?? []).map((existing) => existing.id === inviteId ? updated : existing));
      refreshAudit();
      return updated;
    },
    [api, team, setInvites, refreshAudit]
  );
  const rolesData = rolesRes.data;
  const membersData = membersRes.data;
  const rolesWithCounts = React2.useMemo(() => {
    if (!rolesData) return rolesData;
    if (!membersData) return rolesData;
    return rolesData.map((role) => ({ ...role, memberCount: countMembers(membersData, role.id) }));
  }, [rolesData, membersData]);
  const value = React2.useMemo(
    () => ({
      members: {
        members: membersRes.data ?? [],
        isLoading: membersRes.isLoading,
        error: membersRes.error,
        refetch: membersRes.refetch,
        updateRoles: updateMemberRoles,
        setStatus: setMemberStatus,
        remove: removeMember
      },
      roles: {
        roles: rolesWithCounts ?? [],
        isLoading: rolesRes.isLoading,
        error: rolesRes.error,
        refetch: rolesRes.refetch,
        create: createRole,
        update: updateRole,
        remove: removeRole
      },
      invites: {
        invites: invitesRes.data ?? [],
        isLoading: invitesRes.isLoading,
        error: invitesRes.error,
        refetch: invitesRes.refetch,
        invite,
        revoke: revokeInvite,
        resend: resendInvite
      },
      audit: {
        entries: auditRes.data ?? [],
        isLoading: auditRes.isLoading,
        error: auditRes.error,
        refetch: auditRes.refetch
      }
    }),
    [
      membersRes.data,
      membersRes.isLoading,
      membersRes.error,
      membersRes.refetch,
      rolesWithCounts,
      rolesRes.isLoading,
      rolesRes.error,
      rolesRes.refetch,
      invitesRes.data,
      invitesRes.isLoading,
      invitesRes.error,
      invitesRes.refetch,
      auditRes.data,
      auditRes.isLoading,
      auditRes.error,
      auditRes.refetch,
      updateMemberRoles,
      setMemberStatus,
      removeMember,
      createRole,
      updateRole,
      removeRole,
      invite,
      revokeInvite,
      resendInvite
    ]
  );
  return /* @__PURE__ */ jsx(TeamDataContext.Provider, { value, children });
}
function useTeamData() {
  const value = React2.useContext(TeamDataContext);
  if (!value) throw new Error("useTeamData must be used within a <TeamsProvider>");
  return value;
}
var TeamsContext = React2.createContext(null);
function TeamsProvider({
  api,
  team,
  currentUserId,
  can,
  permissions,
  rolePermissions,
  auditQuery,
  children
}) {
  const value = React2.useMemo(
    () => ({ api, team, currentUserId }),
    [api, team, currentUserId]
  );
  const resolvedCan = can ?? (permissions || rolePermissions ? void 0 : () => true);
  return /* @__PURE__ */ jsx(PermissionProvider, { can: resolvedCan, permissions, rolePermissions, children: /* @__PURE__ */ jsx(TeamsContext.Provider, { value, children: /* @__PURE__ */ jsx(TeamDataProvider, { api, team, auditQuery, children }) }) });
}
function useTeams() {
  const value = React2.useContext(TeamsContext);
  if (!value) throw new Error("useTeams must be used within a <TeamsProvider>");
  return value;
}
function useOptionalTeams() {
  return React2.useContext(TeamsContext);
}
function useTeamCan() {
  return usePermissions().can;
}

// src/data/useMembers.ts
function useMembers() {
  return useTeamData().members;
}

// src/data/useRoles.ts
function useRoles() {
  return useTeamData().roles;
}

// src/data/useInvites.ts
function useInvites() {
  return useTeamData().invites;
}

// src/data/useTeamAudit.ts
function useTeamAudit() {
  return useTeamData().audit;
}
var TEAMS_PACKAGE = "teams";
var log = createLogger(`fe:${TEAMS_PACKAGE}`);
function ConfirmButton({
  onConfirm,
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  destructive = false,
  successMessage,
  errorMessage,
  disabled = false,
  triggerVariant = "ghost",
  triggerClassName,
  size = "sm",
  children
}) {
  const [open, setOpen] = React2.useState(false);
  const [busy, setBusy] = React2.useState(false);
  const confirm = async () => {
    setBusy(true);
    try {
      await onConfirm();
      if (successMessage) notifySuccess(successMessage);
      setOpen(false);
    } catch (error) {
      log.error("teams confirm action failed", { error: String(error) });
      notifyError(error, errorMessage ? { title: errorMessage } : void 0);
    } finally {
      setBusy(false);
    }
  };
  return /* @__PURE__ */ jsxs(AlertDialog, { open, onOpenChange: (next) => !busy && setOpen(next), children: [
    /* @__PURE__ */ jsx(AlertDialogTrigger, { asChild: true, children: /* @__PURE__ */ jsx(Button, { type: "button", variant: triggerVariant, size, className: triggerClassName, disabled, children }) }),
    /* @__PURE__ */ jsxs(AlertDialogContent, { children: [
      /* @__PURE__ */ jsxs(AlertDialogHeader, { children: [
        /* @__PURE__ */ jsx(AlertDialogTitle, { children: title }),
        description ? /* @__PURE__ */ jsx(AlertDialogDescription, { children: description }) : null
      ] }),
      /* @__PURE__ */ jsxs(AlertDialogFooter, { children: [
        /* @__PURE__ */ jsx(AlertDialogCancel, { disabled: busy, children: cancelLabel }),
        /* @__PURE__ */ jsx(
          Button,
          {
            type: "button",
            variant: destructive ? "destructive" : "default",
            disabled: busy,
            onClick: () => void confirm(),
            children: busy ? /* @__PURE__ */ jsxs(Fragment, { children: [
              /* @__PURE__ */ jsx(Spinner, { size: "sm", className: "mr-2 text-current" }),
              confirmLabel,
              "\u2026"
            ] }) : confirmLabel
          }
        )
      ] })
    ] })
  ] });
}
function RoleChips({
  roleIds,
  roles,
  emptyLabel = "No roles",
  className
}) {
  const byId = React2.useMemo(() => new Map(roles.map((role) => [role.id, role])), [roles]);
  if (roleIds.length === 0) {
    return /* @__PURE__ */ jsx("span", { className: cn("text-xs text-muted-foreground", className), children: emptyLabel });
  }
  return /* @__PURE__ */ jsx("div", { className: cn("flex flex-wrap gap-1", className), children: roleIds.map((id) => {
    const role = byId.get(id);
    return /* @__PURE__ */ jsx(Badge, { variant: role?.system ? "secondary" : "outline", className: "font-normal", children: role?.name ?? id }, id);
  }) });
}
function relTime(iso) {
  if (!iso) return "";
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return "";
  return formatRelativeTime(ms, { unit: "milliseconds" });
}
function useAction(fn, options = {}) {
  const [busy, setBusy] = React2.useState(false);
  const fnRef = React2.useRef(fn);
  fnRef.current = fn;
  const optsRef = React2.useRef(options);
  optsRef.current = options;
  const run = React2.useCallback((...args) => {
    const current = fnRef.current;
    if (!current) return;
    const opts = optsRef.current;
    let result;
    try {
      result = current(...args);
    } catch (error) {
      log.error("teams action failed", { error: String(error) });
      notifyError(error, opts.error ? { title: opts.error } : void 0);
      return;
    }
    if (result instanceof Promise) {
      setBusy(true);
      result.then(() => {
        if (opts.success) notifySuccess(opts.success);
      }).catch((error) => {
        log.error("teams action failed", { error: String(error) });
        notifyError(error, opts.error ? { title: opts.error } : void 0);
      }).finally(() => setBusy(false));
    } else if (opts.success) {
      notifySuccess(opts.success);
    }
  }, []);
  return [run, busy];
}
var STATUS_STYLE = {
  active: "",
  invited: "text-muted-foreground",
  suspended: "text-risk-high"
};
var COLUMN_LABELS = {
  member: "Member",
  roles: "Roles",
  status: "Status",
  last_active: "Last active"
};
var STATUS_FACET = {
  column: "status",
  title: "Status",
  options: Object.values(MemberStatus).map((status) => ({
    // Filter on the LABEL, matching what the status column accessor returns —
    // otherwise every option selects nothing.
    value: MEMBER_STATUS_LABEL[status],
    label: MEMBER_STATUS_LABEL[status]
  }))
};
function RolePopover({
  member,
  roles,
  onUpdateRoles
}) {
  const [run, busy] = useAction(onUpdateRoles, { error: "Couldn't update roles." });
  const toggle = (roleId) => {
    const next = member.roleIds.includes(roleId) ? member.roleIds.filter((id) => id !== roleId) : [...member.roleIds, roleId];
    run(member.id, next);
  };
  return /* @__PURE__ */ jsxs(Popover, { children: [
    /* @__PURE__ */ jsx(PopoverTrigger, { asChild: true, children: /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        className: "rounded-md text-left outline-none ring-offset-background hover:opacity-80 focus-visible:ring-2 focus-visible:ring-ring",
        "aria-label": `Edit roles for ${member.name}`,
        children: /* @__PURE__ */ jsx(RoleChips, { roleIds: member.roleIds, roles })
      }
    ) }),
    /* @__PURE__ */ jsxs(PopoverContent, { align: "start", className: "w-64", children: [
      /* @__PURE__ */ jsx("p", { className: "mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground", children: "Roles" }),
      /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1.5", children: [
        roles.map((role) => /* @__PURE__ */ jsxs("label", { className: "flex items-center gap-2 text-sm", children: [
          /* @__PURE__ */ jsx(
            Checkbox,
            {
              checked: member.roleIds.includes(role.id),
              disabled: busy,
              onCheckedChange: () => toggle(role.id)
            }
          ),
          /* @__PURE__ */ jsx("span", { className: "flex-1", children: role.name }),
          role.system ? /* @__PURE__ */ jsx(Badge, { variant: "secondary", children: "system" }) : null
        ] }, role.id)),
        roles.length === 0 ? /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: "No roles defined yet." }) : null
      ] })
    ] })
  ] });
}
function MemberActions({
  member,
  currentUserId,
  canEdit,
  canRemove,
  onSetStatus,
  onRemove
}) {
  const [runStatus, statusBusy] = useAction(onSetStatus, {
    success: "Member status updated",
    error: "Couldn't update the member."
  });
  const isSelf = member.id === currentUserId;
  const suspended = member.status === MemberStatus.SUSPENDED;
  return /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-end gap-1", children: [
    canEdit && onSetStatus && member.status !== MemberStatus.INVITED ? suspended ? /* @__PURE__ */ jsx(
      Button,
      {
        type: "button",
        variant: "ghost",
        size: "sm",
        disabled: statusBusy,
        onClick: () => runStatus(member.id, MemberStatus.ACTIVE),
        children: "Reactivate"
      }
    ) : /* @__PURE__ */ jsx(
      ConfirmButton,
      {
        title: `Suspend ${member.name}?`,
        description: "They keep their membership but lose access until reactivated.",
        confirmLabel: "Suspend",
        successMessage: "Member suspended",
        errorMessage: "Couldn't suspend the member.",
        onConfirm: () => onSetStatus(member.id, MemberStatus.SUSPENDED),
        children: "Suspend"
      }
    ) : null,
    canRemove && onRemove && !isSelf ? /* @__PURE__ */ jsx(
      ConfirmButton,
      {
        destructive: true,
        triggerClassName: "text-risk-high",
        title: `Remove ${member.name}?`,
        description: "They'll lose access to this team immediately. You can invite them again later.",
        confirmLabel: "Remove",
        successMessage: "Member removed",
        errorMessage: "Couldn't remove the member.",
        onConfirm: () => onRemove(member.id),
        children: "Remove"
      }
    ) : null
  ] });
}
function MembersTable({
  members,
  roles,
  currentUserId,
  onUpdateRoles,
  onSetStatus,
  onRemove,
  isLoading,
  error,
  onRetry,
  emptyAction,
  height = "h-[520px]",
  className
}) {
  const can = useTeamCan();
  const canEdit = can(TeamResource.MEMBERS, "edit");
  const canRemove = can(TeamResource.MEMBERS, "delete");
  const columns = React2.useMemo(
    () => [
      {
        id: "member",
        // Name AND email, so one search box finds a member by either.
        accessorFn: (member) => `${member.name} ${member.email ?? ""}`,
        header: ({ column }) => /* @__PURE__ */ jsx(DataTableColumnHeader, { column, title: "Member" }),
        cell: ({ row }) => {
          const member = row.original;
          const isSelf = member.id === currentUserId;
          return /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
            /* @__PURE__ */ jsx(
              Avatar,
              {
                src: member.avatarUrl,
                alt: member.name,
                size: "sm",
                fallback: memberInitials(member)
              }
            ),
            /* @__PURE__ */ jsxs("div", { className: "min-w-0", children: [
              /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1.5", children: [
                /* @__PURE__ */ jsx("span", { className: "truncate text-sm font-medium", children: member.name }),
                isSelf ? /* @__PURE__ */ jsx("span", { className: "text-xs text-muted-foreground", children: "(you)" }) : null
              ] }),
              member.email ? /* @__PURE__ */ jsx("span", { className: "truncate text-xs text-muted-foreground", children: member.email }) : null
            ] })
          ] });
        }
      },
      {
        id: "roles",
        // Role NAMES, not ids, so the search box matches what the chips show.
        accessorFn: (member) => member.roleIds.map((id) => roles.find((role) => role.id === id)?.name ?? id).join(" "),
        header: "Roles",
        enableSorting: false,
        cell: ({ row }) => canEdit && onUpdateRoles ? /* @__PURE__ */ jsx(RolePopover, { member: row.original, roles, onUpdateRoles }) : /* @__PURE__ */ jsx(RoleChips, { roleIds: row.original.roleIds, roles })
      },
      {
        id: "status",
        accessorFn: (member) => MEMBER_STATUS_LABEL[member.status],
        header: ({ column }) => /* @__PURE__ */ jsx(DataTableColumnHeader, { column, title: "Status" }),
        cell: ({ row }) => /* @__PURE__ */ jsx(
          Badge,
          {
            variant: "outline",
            className: cn("font-normal", STATUS_STYLE[row.original.status]),
            children: MEMBER_STATUS_LABEL[row.original.status]
          }
        )
      },
      {
        id: "last_active",
        accessorFn: (member) => member.lastActiveAt ?? "",
        header: ({ column }) => /* @__PURE__ */ jsx(DataTableColumnHeader, { column, title: "Last active" }),
        cell: ({ row }) => /* @__PURE__ */ jsx("span", { className: "text-xs text-muted-foreground", children: relTime(row.original.lastActiveAt) })
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        enableHiding: false,
        cell: ({ row }) => /* @__PURE__ */ jsx(
          MemberActions,
          {
            member: row.original,
            currentUserId,
            canEdit,
            canRemove,
            onSetStatus,
            onRemove
          }
        )
      }
    ],
    [roles, currentUserId, canEdit, canRemove, onUpdateRoles, onSetStatus, onRemove]
  );
  return /* @__PURE__ */ jsx(
    ResourceTable,
    {
      className,
      rows: members,
      columns,
      getRowId: (member) => member.id,
      isLoading,
      error,
      onRetry,
      toolbar: {
        searchColumns: ["member", "roles"],
        searchPlaceholder: "Search members\u2026",
        columnLabels: COLUMN_LABELS
      },
      facets: (table) => /* @__PURE__ */ jsx(TableFacets, { table, configs: [STATUS_FACET] }),
      height,
      emptyTitle: "No members yet",
      emptyDescription: "Invite teammates to collaborate on this team.",
      emptyAction
    }
  );
}
function InviteMemberDialog({
  roles,
  onInvite,
  trigger,
  open,
  onOpenChange,
  defaultRoleIds = []
}) {
  const can = useTeamCan();
  const [internalOpen, setInternalOpen] = React2.useState(false);
  const isControlled = open !== void 0;
  const isOpen = isControlled ? open : internalOpen;
  const setOpen = React2.useCallback(
    (next) => {
      if (!isControlled) setInternalOpen(next);
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange]
  );
  const [email, setEmail] = React2.useState("");
  const [roleIds, setRoleIds] = React2.useState(defaultRoleIds);
  const [message, setMessage] = React2.useState("");
  const [touched, setTouched] = React2.useState(false);
  const [busy, setBusy] = React2.useState(false);
  const [submitError, setSubmitError] = React2.useState(null);
  React2.useEffect(() => {
    if (isOpen) {
      setEmail("");
      setRoleIds(defaultRoleIds);
      setMessage("");
      setTouched(false);
      setSubmitError(null);
    }
  }, [isOpen]);
  if (!can(TeamResource.MEMBERS, "create")) return null;
  const emailValid = isValidEmail(email);
  const toggleRole = (roleId) => setRoleIds((prev) => prev.includes(roleId) ? prev.filter((id) => id !== roleId) : [...prev, roleId]);
  const submit = async (event) => {
    event.preventDefault();
    setTouched(true);
    if (!emailValid) return;
    setBusy(true);
    setSubmitError(null);
    try {
      await onInvite({ email: email.trim(), roleIds, message: message.trim() || void 0 });
      notifySuccess("Invitation sent");
      setOpen(false);
    } catch (error) {
      log.error("teams invite failed", { error: String(error) });
      const response = notifyError(error, { title: "Couldn't send the invitation." });
      setSubmitError(response.error.message || "Couldn't send the invitation.");
    } finally {
      setBusy(false);
    }
  };
  return /* @__PURE__ */ jsxs(Dialog, { open: isOpen, onOpenChange: (next) => !busy && setOpen(next), children: [
    /* @__PURE__ */ jsx(DialogTrigger, { asChild: true, children: trigger ?? /* @__PURE__ */ jsx(Button, { type: "button", children: "Invite member" }) }),
    /* @__PURE__ */ jsx(DialogContent, { children: /* @__PURE__ */ jsxs("form", { onSubmit: (event) => void submit(event), children: [
      /* @__PURE__ */ jsxs(DialogHeader, { children: [
        /* @__PURE__ */ jsx(DialogTitle, { children: "Invite member" }),
        /* @__PURE__ */ jsx(DialogDescription, { children: "Send an invitation email. They join once they accept." })
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "my-4 flex flex-col gap-4", children: [
        /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1.5", children: [
          /* @__PURE__ */ jsx(Label, { htmlFor: "invite-email", children: "Email" }),
          /* @__PURE__ */ jsx(
            Input,
            {
              id: "invite-email",
              type: "email",
              placeholder: "teammate@company.com",
              value: email,
              autoFocus: true,
              onChange: (event) => setEmail(event.target.value),
              onBlur: () => setTouched(true),
              "aria-invalid": touched && !emailValid
            }
          ),
          touched && !emailValid ? /* @__PURE__ */ jsx("span", { className: "text-xs text-destructive", children: "Enter a valid email address." }) : null
        ] }),
        /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1.5", children: [
          /* @__PURE__ */ jsx(Label, { children: "Roles" }),
          /* @__PURE__ */ jsx("div", { className: "flex max-h-56 flex-col gap-3 overflow-y-auto rounded-md border p-2", children: roles.length === 0 ? /* @__PURE__ */ jsx("span", { className: "text-xs text-muted-foreground", children: "No roles defined yet." }) : groupRolesBySource(roles).map((group) => /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-2", children: [
            /* @__PURE__ */ jsx("span", { className: "text-[10px] font-medium uppercase tracking-wide text-muted-foreground", children: group.label }),
            group.roles.map((role) => /* @__PURE__ */ jsxs("label", { className: "flex items-start gap-2 text-sm", children: [
              /* @__PURE__ */ jsx(
                Checkbox,
                {
                  className: "mt-0.5 shrink-0",
                  checked: roleIds.includes(role.id),
                  onCheckedChange: () => toggleRole(role.id)
                }
              ),
              /* @__PURE__ */ jsxs("span", { className: "min-w-0", children: [
                /* @__PURE__ */ jsx("span", { className: "block font-medium", children: role.name }),
                role.description ? /* @__PURE__ */ jsx("span", { className: "block text-xs text-muted-foreground", children: role.description }) : null
              ] })
            ] }, role.id))
          ] }, group.key)) })
        ] }),
        /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1.5", children: [
          /* @__PURE__ */ jsx(Label, { htmlFor: "invite-message", children: "Message (optional)" }),
          /* @__PURE__ */ jsx(
            Textarea,
            {
              id: "invite-message",
              rows: 2,
              placeholder: "Add a note to the invitation email\u2026",
              value: message,
              onChange: (event) => setMessage(event.target.value)
            }
          )
        ] })
      ] }),
      submitError ? /* @__PURE__ */ jsx("p", { role: "alert", className: "mb-2 text-sm text-destructive", children: submitError }) : null,
      /* @__PURE__ */ jsxs(DialogFooter, { children: [
        /* @__PURE__ */ jsx(Button, { type: "button", variant: "ghost", disabled: busy, onClick: () => setOpen(false), children: "Cancel" }),
        /* @__PURE__ */ jsx(Button, { type: "submit", disabled: busy || touched && !emailValid, children: busy ? /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx(Spinner, { size: "sm", className: "mr-2 text-current" }),
          "Sending\u2026"
        ] }) : "Send invite" })
      ] })
    ] }) })
  ] });
}
var STATUS_VARIANT = {
  pending: "secondary",
  accepted: "outline",
  revoked: "destructive",
  expired: "outline"
};
var COLUMN_LABELS2 = {
  email: "Email",
  roles: "Roles",
  status: "Status"
};
function InviteActions({
  invite,
  canRevoke,
  canResend,
  onRevoke,
  onResend
}) {
  const [runResend, resendBusy] = useAction(onResend, {
    success: "Invitation resent",
    error: "Couldn't resend the invitation."
  });
  if (!isInviteActionable(invite) || !canResend && !canRevoke) return null;
  return /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-end gap-1", children: [
    canResend && onResend ? /* @__PURE__ */ jsx(
      Button,
      {
        type: "button",
        variant: "ghost",
        size: "sm",
        disabled: resendBusy,
        onClick: () => runResend(invite.id),
        children: "Resend"
      }
    ) : null,
    canRevoke && onRevoke ? /* @__PURE__ */ jsx(
      ConfirmButton,
      {
        destructive: true,
        triggerClassName: "text-risk-high",
        title: `Revoke invite to ${invite.email}?`,
        description: "The invitation link stops working immediately. You can always send a new invite later.",
        confirmLabel: "Revoke invite",
        successMessage: "Invitation revoked",
        errorMessage: "Couldn't revoke the invitation.",
        onConfirm: () => onRevoke(invite.id),
        children: "Revoke"
      }
    ) : null
  ] });
}
function InvitesTable({
  invites,
  roles,
  onRevoke,
  onResend,
  isLoading,
  error,
  onRetry,
  emptyAction,
  height = "h-[420px]",
  className
}) {
  const can = useTeamCan();
  const canRevoke = can(TeamResource.INVITES, "delete");
  const canResend = can(TeamResource.INVITES, "create");
  const columns = React2.useMemo(
    () => [
      {
        id: "email",
        accessorFn: (invite) => invite.email,
        header: ({ column }) => /* @__PURE__ */ jsx(DataTableColumnHeader, { column, title: "Email" }),
        cell: ({ row }) => /* @__PURE__ */ jsxs("div", { className: "min-w-0", children: [
          /* @__PURE__ */ jsx("div", { className: "text-sm font-medium", children: row.original.email }),
          /* @__PURE__ */ jsx("div", { className: "text-xs text-muted-foreground", children: relTime(row.original.invitedAt) })
        ] })
      },
      {
        id: "roles",
        // Role NAMES, not ids, so the search box matches what the chips show.
        accessorFn: (invite) => invite.roleIds.map((id) => roles.find((role) => role.id === id)?.name ?? id).join(" "),
        header: "Roles",
        enableSorting: false,
        cell: ({ row }) => /* @__PURE__ */ jsx(RoleChips, { roleIds: row.original.roleIds, roles })
      },
      {
        id: "status",
        accessorFn: (invite) => INVITE_STATUS_LABEL[invite.status],
        header: ({ column }) => /* @__PURE__ */ jsx(DataTableColumnHeader, { column, title: "Status" }),
        cell: ({ row }) => /* @__PURE__ */ jsx(Badge, { variant: STATUS_VARIANT[row.original.status], className: "font-normal", children: INVITE_STATUS_LABEL[row.original.status] })
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        enableHiding: false,
        cell: ({ row }) => /* @__PURE__ */ jsx(
          InviteActions,
          {
            invite: row.original,
            canRevoke,
            canResend,
            onRevoke,
            onResend
          }
        )
      }
    ],
    [roles, canRevoke, canResend, onRevoke, onResend]
  );
  return /* @__PURE__ */ jsx(
    ResourceTable,
    {
      className,
      rows: invites,
      columns,
      getRowId: (invite) => invite.id,
      isLoading,
      error,
      onRetry,
      toolbar: {
        searchColumns: ["email", "roles", "status"],
        searchPlaceholder: "Search invites\u2026",
        columnLabels: COLUMN_LABELS2
      },
      height,
      emptyTitle: "No pending invites",
      emptyDescription: "Invitations you send will appear here until accepted.",
      emptyAction
    }
  );
}
function RolesList({
  roles,
  selectedId,
  onSelect,
  onCreate,
  onDelete,
  className
}) {
  const can = useTeamCan();
  const canCreate = can(TeamResource.ROLES, "create");
  const canDelete = can(TeamResource.ROLES, "delete");
  const sections = groupRolesBySource(roles);
  const renderRole = (role) => {
    const selected = role.id === selectedId;
    return /* @__PURE__ */ jsx("li", { children: /* @__PURE__ */ jsxs(
      "div",
      {
        className: cn(
          "flex items-center gap-2 rounded-md border p-2 transition-colors",
          selected ? "border-brand bg-muted" : "hover:bg-muted/60"
        ),
        children: [
          /* @__PURE__ */ jsxs(
            "button",
            {
              type: "button",
              onClick: () => onSelect?.(role.id),
              className: "min-w-0 flex-1 text-left outline-none",
              children: [
                /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1.5", children: [
                  /* @__PURE__ */ jsx("span", { className: "truncate text-sm font-medium", children: role.name }),
                  role.system ? /* @__PURE__ */ jsx(Badge, { variant: "secondary", children: "system" }) : null
                ] }),
                role.description ? /* @__PURE__ */ jsx("p", { className: "mt-0.5 text-xs text-muted-foreground", children: role.description }) : null,
                /* @__PURE__ */ jsxs("div", { className: "mt-0.5 flex flex-wrap gap-2 text-xs text-muted-foreground", children: [
                  /* @__PURE__ */ jsxs("span", { children: [
                    countPermissions(role.permissions),
                    " grants"
                  ] }),
                  typeof role.memberCount === "number" ? /* @__PURE__ */ jsxs("span", { children: [
                    "\xB7 ",
                    role.memberCount,
                    " members"
                  ] }) : null
                ] })
              ]
            }
          ),
          canDelete && onDelete && !role.system ? /* @__PURE__ */ jsx(
            ConfirmButton,
            {
              destructive: true,
              triggerClassName: "text-risk-high",
              title: `Delete \u201C${role.name}\u201D?`,
              description: "This role and its permissions will be removed. Members currently holding it lose those grants. This can't be undone.",
              confirmLabel: "Delete role",
              successMessage: "Role deleted",
              errorMessage: "Couldn't delete the role.",
              onConfirm: () => onDelete(role.id),
              children: "Delete"
            }
          ) : null
        ]
      }
    ) }, role.id);
  };
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col gap-2", className), children: [
    canCreate && onCreate ? /* @__PURE__ */ jsx(Button, { type: "button", variant: "outline", size: "sm", className: "self-start", onClick: onCreate, children: "New role" }) : null,
    sections.length === 0 ? /* @__PURE__ */ jsx(EmptyState, { size: "sm", title: "No roles", description: "Create a role to start granting permissions." }) : /* @__PURE__ */ jsx("ul", { className: "flex flex-col gap-1", children: sections.map((section) => /* @__PURE__ */ jsxs(React2.Fragment, { children: [
      /* @__PURE__ */ jsxs("li", { role: "presentation", className: "flex items-center gap-2 px-1 pb-0.5 pt-3 first:pt-0", children: [
        /* @__PURE__ */ jsx("span", { className: "text-[10px] font-medium uppercase tracking-wide text-muted-foreground", children: section.label }),
        /* @__PURE__ */ jsx("span", { className: "h-px flex-1 bg-border" })
      ] }),
      section.roles.map((role) => renderRole(role))
    ] }, section.key)) })
  ] });
}
function RoleEditor({ role, readOnly, ...rest }) {
  const can = useTeamCan();
  const locked = Boolean(readOnly) || !can(TeamResource.ROLES, "edit");
  return /* @__PURE__ */ jsx(RoleEditor$1, { role, readOnly: locked, ...rest });
}
function RoleMembers({ roleId, members, onUpdateRoles, className }) {
  const can = useTeamCan();
  const canAssign = can(TeamResource.MEMBERS, "edit");
  const [runAssign, assignBusy] = useAction(onUpdateRoles, {
    success: "Member assigned to role",
    error: "Couldn't assign the role."
  });
  const [runUnassign, unassignBusy] = useAction(onUpdateRoles, {
    success: "Member removed from role",
    error: "Couldn't update the role."
  });
  const busy = assignBusy || unassignBusy;
  const assigned = members.filter((member) => member.roleIds.includes(roleId));
  const assignable = members.filter((member) => !member.roleIds.includes(roleId));
  const assign = (member) => {
    runAssign(member.id, [...member.roleIds, roleId]);
  };
  const unassign = (member) => {
    runUnassign(member.id, member.roleIds.filter((id) => id !== roleId));
  };
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col gap-2", className), children: [
    /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between", children: [
      /* @__PURE__ */ jsxs("span", { className: "text-sm font-medium", children: [
        "Users with this role (",
        assigned.length,
        ")"
      ] }),
      canAssign && onUpdateRoles ? /* @__PURE__ */ jsxs(DropdownMenu, { children: [
        /* @__PURE__ */ jsx(DropdownMenuTrigger, { asChild: true, children: /* @__PURE__ */ jsx(Button, { type: "button", variant: "outline", size: "sm", disabled: busy || assignable.length === 0, children: "Assign member" }) }),
        /* @__PURE__ */ jsx(DropdownMenuContent, { align: "end", className: "max-h-72 w-56 overflow-y-auto", children: assignable.length === 0 ? /* @__PURE__ */ jsx(DropdownMenuItem, { disabled: true, children: "Everyone already has this role" }) : assignable.map((member) => /* @__PURE__ */ jsxs(DropdownMenuItem, { onSelect: () => assign(member), children: [
          /* @__PURE__ */ jsx(Avatar, { size: "sm", src: member.avatarUrl, alt: member.name, fallback: memberInitials(member) }),
          /* @__PURE__ */ jsx("span", { className: "min-w-0 flex-1 truncate", children: member.name || member.email || member.id })
        ] }, member.id)) })
      ] }) : null
    ] }),
    assigned.length === 0 ? /* @__PURE__ */ jsx(EmptyState, { size: "sm", title: "No users", description: "Assign this role to members to grant its permissions." }) : /* @__PURE__ */ jsx("ul", { className: "flex flex-col gap-1", children: assigned.map((member) => /* @__PURE__ */ jsxs(
      "li",
      {
        className: "flex items-center gap-2 rounded-md border p-2",
        children: [
          /* @__PURE__ */ jsx(Avatar, { size: "sm", src: member.avatarUrl, alt: member.name, fallback: memberInitials(member) }),
          /* @__PURE__ */ jsxs("div", { className: "min-w-0 flex-1", children: [
            /* @__PURE__ */ jsx("div", { className: "truncate text-sm font-medium", children: member.name || member.email || member.id }),
            member.email ? /* @__PURE__ */ jsx("div", { className: "truncate text-xs text-muted-foreground", children: member.email }) : null
          ] }),
          canAssign && onUpdateRoles ? /* @__PURE__ */ jsx(
            Button,
            {
              type: "button",
              variant: "ghost",
              size: "sm",
              disabled: busy,
              "aria-label": `Remove role from ${member.name || member.email || member.id}`,
              onClick: () => unassign(member),
              children: "Remove"
            }
          ) : null
        ]
      },
      member.id
    )) })
  ] });
}
var BLANK_ROLE = { id: "__new__", name: "", permissions: {} };
function RolesPanel({
  resourceGroups,
  actions,
  scopes,
  defaultRoles,
  className
}) {
  const { roles, isLoading, error, refetch, create, update, remove } = useRoles();
  const members = useMembers();
  const [selectedId, setSelectedId] = React2.useState(null);
  const [creating, setCreating] = React2.useState(false);
  const displayRoles = React2.useMemo(() => mergeDefaultRoles(roles, defaultRoles), [roles, defaultRoles]);
  const selectedRole = creating ? BLANK_ROLE : displayRoles.find((role) => role.id === selectedId) ?? null;
  const selectedIsRealRole = Boolean(
    selectedRole && !creating && roles.some((role) => role.id === selectedRole.id)
  );
  const handleSave = async (draft) => {
    try {
      if (creating) {
        const created = await create(draft);
        setCreating(false);
        setSelectedId(created.id);
        notifySuccess("Role created", { description: `\u201C${created.name}\u201D is ready to assign.` });
      } else if (selectedId) {
        const updated = await update(selectedId, draft);
        notifySuccess("Role updated", { description: `Saved changes to \u201C${updated.name}\u201D.` });
      }
    } catch (err) {
      notifyError(err, { title: "Couldn't save the role." });
    }
  };
  if (error) return /* @__PURE__ */ jsx(ErrorState, { error, onRetry: refetch });
  if (isLoading) {
    return /* @__PURE__ */ jsxs("div", { className: "grid gap-4 sm:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]", "aria-busy": true, children: [
      /* @__PURE__ */ jsx(Skeleton, { className: "h-48 w-full" }),
      /* @__PURE__ */ jsx(Skeleton, { className: "h-48 w-full" })
    ] });
  }
  return /* @__PURE__ */ jsxs("div", { className: cn("grid gap-4 sm:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]", className), children: [
    /* @__PURE__ */ jsx(
      RolesList,
      {
        roles: displayRoles,
        selectedId: creating ? null : selectedId,
        onSelect: (id) => {
          setCreating(false);
          setSelectedId(id);
        },
        onCreate: () => {
          setCreating(true);
          setSelectedId(null);
        },
        onDelete: async (id) => {
          await remove(id);
          if (id === selectedId) setSelectedId(null);
        }
      }
    ),
    selectedRole ? /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-6", children: [
      /* @__PURE__ */ jsx(
        RoleEditor,
        {
          role: selectedRole,
          resourceGroups,
          actions,
          scopes,
          readOnly: selectedRole.system,
          onSave: handleSave,
          onCancel: creating ? () => setCreating(false) : void 0
        },
        creating ? "__new__" : selectedRole.id
      ),
      !creating ? selectedIsRealRole ? /* @__PURE__ */ jsx(
        RoleMembers,
        {
          roleId: selectedRole.id,
          members: members.members,
          onUpdateRoles: members.updateRoles
        }
      ) : /* @__PURE__ */ jsx("p", { className: "rounded-md border border-dashed p-3 text-xs text-muted-foreground", children: "This is an inherited platform default. Save it as a team role first to assign members to it." }) : null
    ] }) : /* @__PURE__ */ jsx("div", { className: "flex items-center justify-center rounded-md border border-dashed p-6 text-sm text-muted-foreground", children: "Select a role to edit, or create a new one." })
  ] });
}
function TeamAuditLog({
  entries,
  isLoading,
  error,
  onRetry,
  className
}) {
  if (error) return /* @__PURE__ */ jsx(ErrorState, { error, onRetry });
  if (isLoading) {
    return /* @__PURE__ */ jsx("div", { className: "flex flex-col gap-2", "aria-busy": true, children: Array.from({ length: 5 }).map((_, index) => /* @__PURE__ */ jsx(Skeleton, { className: "h-14 w-full" }, index)) });
  }
  if (entries.length === 0) {
    return /* @__PURE__ */ jsx(
      EmptyState,
      {
        title: "No activity yet",
        description: "Member, role, and invite changes will appear here with attribution."
      }
    );
  }
  return /* @__PURE__ */ jsx(AuditTrail, { events: toActivityLogEvents(entries), className });
}
function TeamSwitcher({
  memberships,
  current,
  onSwitch,
  className
}) {
  const byKey = React2.useMemo(
    () => new Map(memberships.map((membership) => [teamKey(membership.team), membership.team])),
    [memberships]
  );
  const handleChange = (key) => {
    const team = byKey.get(key);
    if (team && !sameTeam(team, current)) onSwitch(team);
  };
  return /* @__PURE__ */ jsxs(Select, { value: teamKey(current), onValueChange: handleChange, children: [
    /* @__PURE__ */ jsx(SelectTrigger, { className, "aria-label": "Switch team", children: /* @__PURE__ */ jsx(SelectValue, { placeholder: "Select team" }) }),
    /* @__PURE__ */ jsx(SelectContent, { children: memberships.map((membership) => {
      const { team } = membership;
      return /* @__PURE__ */ jsx(SelectItem, { value: teamKey(team), children: /* @__PURE__ */ jsxs("span", { className: "flex items-center gap-2", children: [
        /* @__PURE__ */ jsx(
          Avatar,
          {
            src: team.avatarUrl,
            alt: team.name,
            size: "sm",
            fallback: memberInitials({ name: team.name })
          }
        ),
        /* @__PURE__ */ jsx("span", { children: team.name }),
        typeof team.memberCount === "number" ? /* @__PURE__ */ jsxs("span", { className: "text-xs text-muted-foreground", children: [
          "\xB7 ",
          team.memberCount
        ] }) : null
      ] }) }, teamKey(team));
    }) })
  ] });
}
function PanelInner({
  resourceGroups,
  actions,
  scopes,
  defaultRoles,
  memberships,
  onSwitchTeam,
  title,
  description,
  defaultTab = "members",
  className
}) {
  const { team, currentUserId } = useTeams();
  const members = useMembers();
  const roles = useRoles();
  const invites = useInvites();
  const audit = useTeamAudit();
  const [tab, setTab] = React2.useState(defaultTab);
  const allRoles = React2.useMemo(
    () => mergeDefaultRoles(roles.roles, defaultRoles),
    [roles.roles, defaultRoles]
  );
  const inviteDialog = /* @__PURE__ */ jsx(InviteMemberDialog, { roles: allRoles, onInvite: invites.invite });
  const heading = title === void 0 ? team.name : title;
  const hasHeading = heading != null && heading !== "";
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col gap-4", className), children: [
    /* @__PURE__ */ jsxs("header", { className: "flex flex-wrap items-center gap-3", children: [
      hasHeading || description ? /* @__PURE__ */ jsxs("div", { className: "mr-auto min-w-0", children: [
        hasHeading ? /* @__PURE__ */ jsx("h2", { className: "text-lg font-semibold", children: heading }) : null,
        description ? /* @__PURE__ */ jsx("p", { className: "text-sm text-muted-foreground", children: description }) : null
      ] }) : null,
      memberships && memberships.length > 1 && onSwitchTeam ? /* @__PURE__ */ jsx(
        TeamSwitcher,
        {
          memberships,
          current: team,
          onSwitch: onSwitchTeam,
          className: "w-56"
        }
      ) : null,
      /* @__PURE__ */ jsx("div", { className: "ml-auto", children: inviteDialog })
    ] }),
    /* @__PURE__ */ jsxs(Tabs, { value: tab, onValueChange: (value) => setTab(value), children: [
      /* @__PURE__ */ jsxs(TabsList, { children: [
        /* @__PURE__ */ jsx(TabsTrigger, { value: "members", children: "Members" }),
        /* @__PURE__ */ jsx(TabsTrigger, { value: "roles", children: "Roles" }),
        /* @__PURE__ */ jsx(TabsTrigger, { value: "invites", children: "Invites" }),
        /* @__PURE__ */ jsx(TabsTrigger, { value: "audit", children: "Audit log" })
      ] }),
      /* @__PURE__ */ jsx(TabsContent, { value: "members", className: "pt-4", children: /* @__PURE__ */ jsx(
        MembersTable,
        {
          members: members.members,
          roles: allRoles,
          currentUserId,
          isLoading: members.isLoading,
          error: members.error,
          onRetry: members.refetch,
          onUpdateRoles: members.updateRoles,
          onSetStatus: members.setStatus,
          onRemove: members.remove,
          emptyAction: inviteDialog
        }
      ) }),
      /* @__PURE__ */ jsx(TabsContent, { value: "roles", className: "pt-4", children: /* @__PURE__ */ jsx(
        RolesPanel,
        {
          resourceGroups,
          actions,
          scopes,
          defaultRoles
        }
      ) }),
      /* @__PURE__ */ jsx(TabsContent, { value: "invites", className: "pt-4", children: /* @__PURE__ */ jsx(
        InvitesTable,
        {
          invites: invites.invites,
          roles: allRoles,
          isLoading: invites.isLoading,
          error: invites.error,
          onRetry: invites.refetch,
          onRevoke: invites.revoke,
          onResend: invites.resend,
          emptyAction: inviteDialog
        }
      ) }),
      /* @__PURE__ */ jsx(TabsContent, { value: "audit", className: "pt-4", children: /* @__PURE__ */ jsx(
        TeamAuditLog,
        {
          entries: audit.entries,
          isLoading: audit.isLoading,
          error: audit.error,
          onRetry: audit.refetch
        }
      ) })
    ] })
  ] });
}
function TeamManagementPanel(props) {
  const {
    api,
    team,
    currentUserId,
    can,
    permissions,
    rolePermissions,
    resourceGroups,
    actions,
    scopes,
    defaultRoles,
    memberships,
    onSwitchTeam,
    title,
    description,
    defaultTab,
    className
  } = props;
  return /* @__PURE__ */ jsx(ErrorBoundary, { boundary: "teams:management-panel", children: /* @__PURE__ */ jsx(
    TeamsProvider,
    {
      api,
      team,
      currentUserId,
      can,
      permissions,
      rolePermissions,
      children: /* @__PURE__ */ jsx(
        PanelInner,
        {
          resourceGroups,
          actions,
          scopes,
          defaultRoles,
          memberships,
          onSwitchTeam,
          title,
          description,
          defaultTab,
          className
        }
      )
    }
  ) });
}
function rows(data) {
  if (Array.isArray(data)) return data;
  const items = data?.items;
  return Array.isArray(items) ? items : [];
}
var str = (v) => typeof v === "string" && v !== "" ? v : void 0;
var strs = (v) => Array.isArray(v) ? v.filter((x) => typeof x === "string") : [];
function toIso(v) {
  if (v == null) return void 0;
  const n = typeof v === "number" ? v : typeof v === "string" && v.trim() !== "" ? Number(v) : NaN;
  if (Number.isFinite(n)) return new Date(n < 1e12 ? n * 1e3 : n).toISOString();
  return typeof v === "string" ? v : void 0;
}
function memberFromWire(w) {
  return {
    id: str(w.user_id) ?? str(w.id) ?? "",
    name: str(w.display_name) ?? str(w.email) ?? str(w.user_id) ?? str(w.id) ?? "Unknown member",
    email: str(w.email),
    avatarUrl: str(w.avatar_url),
    roleIds: strs(w.role_ids ?? w.roleIds),
    status: str(w.status) ?? "active",
    joinedAt: toIso(w.joined_at ?? w.created_at),
    lastActiveAt: toIso(w.last_active_at)
  };
}
function roleFromWire(w) {
  return {
    id: str(w.id) ?? str(w.role_id) ?? "",
    name: str(w.name) ?? str(w.id) ?? "Role",
    description: str(w.description),
    permissions: w.permissions ?? w.role_permissions ?? {},
    system: Boolean(w.system ?? w.is_system),
    memberCount: typeof w.member_count === "number" ? w.member_count : typeof w.memberCount === "number" ? w.memberCount : void 0
  };
}
function inviteFromWire(w) {
  return {
    id: str(w.id) ?? str(w.invite_id) ?? "",
    email: str(w.email) ?? "",
    roleIds: strs(w.role_ids ?? w.roleIds),
    status: str(w.status) ?? "pending",
    invitedBy: str(w.invited_by),
    invitedAt: toIso(w.invited_at ?? w.created_at),
    expiresAt: toIso(w.expires_at)
  };
}
function auditFromWire(w) {
  return {
    id: str(w.id) ?? "",
    action: str(w.action) ?? str(w.function_name) ?? "",
    summary: str(w.summary) ?? str(w.message_summary) ?? "",
    at: toIso(w.at ?? w.created_at) ?? "",
    actor: str(w.actor) ?? str(w.created_by),
    target: str(w.target) ?? str(w.payload_summary),
    severity: str(w.severity),
    metadata: w.metadata ?? void 0
  };
}
function teamRefFromWire(w) {
  return {
    id: str(w.id) ?? str(w.team_id) ?? "",
    name: str(w.name) ?? str(w.id) ?? "Team",
    tenant: {
      customerId: str(w.customer_id) ?? null,
      orgId: str(w.org_id) ?? null,
      tenantId: str(w.tenant_id) ?? null
    },
    avatarUrl: str(w.avatar_url)
  };
}
function membershipFromWire(w) {
  const team = w.team;
  const id = str(team?.id) ?? str(w.team_id) ?? str(w.id) ?? "";
  const name = str(team?.name) ?? str(w.team_name) ?? id;
  const tenant = team?.tenant ?? w.tenant ?? {
    org_id: str(w.org_id),
    tenant_id: str(w.tenant_id)
  };
  return {
    team: { id, name, tenant, avatarUrl: str(team?.avatar_url ?? w.avatar_url) },
    roleIds: strs(w.role_ids ?? w.roleIds)
  };
}
function createApiTeamsApi(client, options = {}) {
  const base = options.basePath ?? "/teams";
  const teamPath = (team) => `${base}/${encodeURIComponent(team.id)}`;
  return {
    // ── Teams ──────────────────────────────────────────────────────────────────
    async listTeams() {
      return rows(unwrapData(await client.get(base))).map(teamRefFromWire);
    },
    async createTeam(input) {
      return teamRefFromWire(
        unwrapData(
          await client.post(base, {
            name: input.name,
            description: input.description
          })
        )
      );
    },
    // ── Members ──────────────────────────────────────────────────────────────
    async listMembers(team) {
      return rows(unwrapData(await client.get(`${teamPath(team)}/members`))).map(memberFromWire);
    },
    async updateMemberRoles(team, memberId, roleIds) {
      return memberFromWire(
        unwrapData(
          await client.patch(`${teamPath(team)}/members/${encodeURIComponent(memberId)}`, {
            role_ids: roleIds
          })
        )
      );
    },
    async setMemberStatus(team, memberId, status) {
      return memberFromWire(
        unwrapData(
          await client.patch(`${teamPath(team)}/members/${encodeURIComponent(memberId)}`, {
            status
          })
        )
      );
    },
    async removeMember(team, memberId) {
      unwrapData(await client.delete(`${teamPath(team)}/members/${encodeURIComponent(memberId)}`));
    },
    // ── Roles ────────────────────────────────────────────────────────────────
    async listRoles(team) {
      return rows(unwrapData(await client.get(`${teamPath(team)}/roles`))).map(roleFromWire);
    },
    async createRole(team, input) {
      return roleFromWire(unwrapData(await client.post(`${teamPath(team)}/roles`, input)));
    },
    async updateRole(team, roleId, patch) {
      return roleFromWire(
        unwrapData(await client.patch(`${teamPath(team)}/roles/${encodeURIComponent(roleId)}`, patch))
      );
    },
    async deleteRole(team, roleId) {
      unwrapData(await client.delete(`${teamPath(team)}/roles/${encodeURIComponent(roleId)}`));
    },
    // ── Invites ──────────────────────────────────────────────────────────────
    async listInvites(team) {
      return rows(unwrapData(await client.get(`${teamPath(team)}/invites`))).map(inviteFromWire);
    },
    async inviteMember(team, input) {
      return inviteFromWire(
        unwrapData(
          await client.post(`${teamPath(team)}/invites`, {
            email: input.email,
            role_ids: input.roleIds,
            message: input.message
          })
        )
      );
    },
    async revokeInvite(team, inviteId) {
      unwrapData(await client.delete(`${teamPath(team)}/invites/${encodeURIComponent(inviteId)}`));
    },
    async resendInvite(team, inviteId) {
      return inviteFromWire(
        unwrapData(await client.post(`${teamPath(team)}/invites/${encodeURIComponent(inviteId)}/resend`))
      );
    },
    // ── Audit ────────────────────────────────────────────────────────────────
    async listAuditEvents(team, query = {}) {
      const res = unwrapResponse(
        await client.get(`${teamPath(team)}/audit`, {
          query: { limit: query.limit, cursor: query.cursor }
        })
      );
      return rows(res.data).map(auditFromWire);
    },
    // ── Memberships (team switcher) ──────────────────────────────────────────
    async listMemberships(userId) {
      return rows(unwrapData(await client.get(`${base}/memberships`, { query: { userId } }))).map(
        membershipFromWire
      );
    }
  };
}

export { InviteMemberDialog, InvitesTable, MembersTable, RoleChips, RoleEditor, RoleMembers, RolesList, RolesPanel, TEAMS_PACKAGE, TeamAuditLog, TeamDataProvider, TeamManagementPanel, TeamSwitcher, TeamsProvider, createApiTeamsApi, groupRolesBySource, mergeDefaultRoles, useAsyncResource, useInvites, useMembers, useOptionalTeams, useRoles, useTeamAudit, useTeamCan, useTeamData, useTeams };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map